import type { Locale } from '@/shared/i18n/types';

type ScheduleMessages = {
  shiftOverview: string;
  weeklyHours: string;
  addTimeSlot: string;
  day: string;
  open: string;
  closed: string;
  hours: string;
  editSlot: string;
  deleteSlot: string;
  extraordinarySection: string;
  addException: string;
  dateRange: string;
  type: string;
  closedOpenDay: string;
  timeSlots: string;
  cuttingMin: string;
  maxGuests: string;
  noShifts: string;
  emptyWeeklyHours: string;
  emptyExtraordinary: string;
  createForAllDays: string;
  resetConfiguration: string;
  saveConfiguration: string;
  resetConfirmTitle: string;
  resetConfirmDesc: string;
  resetProceed: string;
  tags: string;
  tagsPlaceholder: string;
  allowOverbook: string;
  alertMessage: string;
  alertMessagePlaceholder: string;
  days: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  addTimeAllDays: {
    title: string;
    subtitle: string;
    chosenTime: string;
    duration: string;
    startAt: string;
    endsAt: string;
    allOkDesc: string;
    saveOnAllDays: string;
  };
  slotTime: {
    title: string;
    subtitle: string;
    allOk: string;
    allOkDesc: string;
    saveTime: string;
    timeRangeInvalid: string;
  };
  extraordinary: {
    badge: string;
    createRange: string;
    editRange: string;
    description: string;
    descriptionPlaceholder: string;
    rangeDate: string;
    rangeDatePlaceholder: string;
    clearDates: string;
    applyDates: string;
    open: string;
    create: string;
    update: string;
    validation: string;
    dateRequired: string;
    dateOrder: string;
    timeRangeInvalid: string;
    addConfiguration: string;
    saveConfiguration: string;
    addTimeSlot: string;
    deleteRange: string;
    deleteConfirmTitle: string;
    deleteConfirmDesc: string;
    toast: {
      createSuccess: string;
      updateSuccess: string;
      saveError: string;
      deleteSuccess: string;
      deleteError: string;
    };
  };
  toast: {
    saveSuccess: string;
    saveError: string;
    resetSuccess: string;
    resetError: string;
    addSlotSuccess: string;
    addSlotError: string;
    deleteSlotSuccess: string;
    deleteSlotError: string;
    updateTimeSuccess: string;
    updateTimeError: string;
  };
};

export const scheduleLocaleOverrides: Partial<Record<Locale, ScheduleMessages>> =
  {
    es: {
      shiftOverview: 'Resumen de turnos',
      weeklyHours: 'Horario semanal',
      addTimeSlot: 'Añadir franja horaria',
      day: 'Día',
      open: 'Abierto',
      closed: 'Cerrado',
      hours: 'Horas',
      editSlot: 'Editar franja',
      deleteSlot: 'Eliminar franja',
      extraordinarySection: 'Aperturas / cierres extraordinarios',
      addException: 'Añadir excepción',
      dateRange: 'Rango de fechas',
      type: 'Tipo',
      closedOpenDay: 'Cerrado/Abierto',
      timeSlots: 'Franjas horarias',
      cuttingMin: 'Corte (min)',
      maxGuests: 'Comensales máx.',
      noShifts: 'No hay turnos configurados.',
      emptyWeeklyHours:
        'No hay horario semanal. Usa + para crear la configuración para todos los días.',
      emptyExtraordinary:
        'No hay aperturas o cierres extraordinarios configurados.',
      createForAllDays: 'Crear configuración general para todos los días',
      resetConfiguration: 'Restablecer configuración',
      saveConfiguration: 'Guardar configuración',
      resetConfirmTitle: '¿Restablecer configuración?',
      resetConfirmDesc:
        'Esto eliminará toda la configuración de horarios de este formulario.',
      resetProceed: 'Continuar',
      tags: 'Etiquetas',
      tagsPlaceholder: 'Introduce etiquetas separadas por comas',
      allowOverbook: 'Permitir overbooking',
      alertMessage: 'Mensaje de alerta',
      alertMessagePlaceholder:
        'Introduce un mensaje de alerta para esta franja horaria',
      days: {
        monday: 'Lunes',
        tuesday: 'Martes',
        wednesday: 'Miércoles',
        thursday: 'Jueves',
        friday: 'Viernes',
        saturday: 'Sábado',
        sunday: 'Domingo',
      },
      addTimeAllDays: {
        title: 'Añadir horario a todos los días',
        subtitle:
          'Los horarios disponibles respetan las franjas del local y las ya configuradas.',
        chosenTime: 'Horario elegido',
        duration: 'Duración: {{duration}}',
        startAt: '1. Empieza a las',
        endsAt: '2. Termina a las',
        allOkDesc: 'Puedes guardar este horario para todos los días.',
        saveOnAllDays: 'Guardar en todos los días',
      },
      slotTime: {
        title: 'Franja horaria',
        subtitle: 'Primero elige cuándo empieza. Luego cuándo termina.',
        allOk: 'Todo correcto',
        allOkDesc: 'Puedes guardar este horario.',
        saveTime: 'Guardar horario',
        timeRangeInvalid: 'La hora de fin debe ser posterior a la de inicio.',
      },
      extraordinary: {
        badge: 'Configuración extraordinaria',
        createRange: 'Crear intervalo',
        editRange: 'Editar intervalo',
        description: 'Descripción',
        descriptionPlaceholder: 'Ej. Ferragosto, evento privado...',
        rangeDate: 'Rango de fechas',
        rangeDatePlaceholder: 'Seleccionar rango de fechas',
        clearDates: 'Borrar',
        applyDates: 'Aplicar',
        open: 'Abierto',
        create: 'Crear',
        update: 'Actualizar',
        validation: 'La descripción y el rango de fechas son obligatorios.',
        dateRequired: 'Selecciona un rango de fechas.',
        dateOrder: 'La fecha de fin no puede ser anterior a la de inicio.',
        timeRangeInvalid: 'La hora de fin debe ser posterior a la de inicio.',
        addConfiguration: 'Añadir configuración extraordinaria',
        saveConfiguration: 'Actualizar configuración',
        addTimeSlot: 'Añadir franja horaria',
        deleteRange: 'Eliminar intervalo',
        deleteConfirmTitle: '¿Eliminar intervalo extraordinario?',
        deleteConfirmDesc:
          'Esto eliminará permanentemente esta configuración de fechas.',
        toast: {
          createSuccess: 'Intervalo extraordinario creado.',
          updateSuccess: 'Intervalo extraordinario actualizado.',
          saveError: 'No se pudo guardar el intervalo extraordinario.',
          deleteSuccess: 'Intervalo extraordinario eliminado.',
          deleteError: 'No se pudo eliminar el intervalo extraordinario.',
        },
      },
      toast: {
        saveSuccess: 'Configuración de horarios guardada.',
        saveError: 'No se pudo guardar la configuración.',
        resetSuccess: 'Configuración de horarios restablecida.',
        resetError: 'No se pudo restablecer la configuración.',
        addSlotSuccess: 'Franja horaria añadida.',
        addSlotError: 'No se pudo añadir la franja horaria.',
        deleteSlotSuccess: 'Franja horaria eliminada.',
        deleteSlotError: 'No se pudo eliminar la franja horaria.',
        updateTimeSuccess: 'Franja horaria actualizada.',
        updateTimeError: 'No se pudo actualizar la franja horaria.',
      },
    },
    fr: {
      shiftOverview: 'Aperçu des services',
      weeklyHours: 'Horaires hebdomadaires',
      addTimeSlot: 'Ajouter un créneau',
      day: 'Jour',
      open: 'Ouvert',
      closed: 'Fermé',
      hours: 'Horaires',
      editSlot: 'Modifier le créneau',
      deleteSlot: 'Supprimer le créneau',
      extraordinarySection: 'Ouvertures / fermetures exceptionnelles',
      addException: 'Ajouter une exception',
      dateRange: 'Plage de dates',
      type: 'Type',
      closedOpenDay: 'Fermé/Ouvert',
      timeSlots: 'Créneaux horaires',
      cuttingMin: 'Découpage (min)',
      maxGuests: 'Couverts max',
      noShifts: 'Aucun service configuré.',
      emptyWeeklyHours:
        'Aucun horaire hebdomadaire. Utilisez + pour créer la configuration pour tous les jours.',
      emptyExtraordinary:
        'Aucune ouverture ou fermeture exceptionnelle configurée.',
      createForAllDays:
        'Créer une configuration générale pour tous les jours',
      resetConfiguration: 'Réinitialiser la configuration',
      saveConfiguration: 'Enregistrer la configuration',
      resetConfirmTitle: 'Réinitialiser la configuration ?',
      resetConfirmDesc:
        'Cela supprimera tous les paramètres horaires de ce formulaire.',
      resetProceed: 'Continuer',
      tags: 'Tags',
      tagsPlaceholder: 'Saisissez les tags séparés par des virgules',
      allowOverbook: 'Autoriser le surbooking',
      alertMessage: "Message d'alerte",
      alertMessagePlaceholder:
        'Saisissez un message d\'alerte pour ce créneau',
      days: {
        monday: 'Lundi',
        tuesday: 'Mardi',
        wednesday: 'Mercredi',
        thursday: 'Jeudi',
        friday: 'Vendredi',
        saturday: 'Samedi',
        sunday: 'Dimanche',
      },
      addTimeAllDays: {
        title: 'Ajouter un horaire à tous les jours',
        subtitle:
          'Les horaires disponibles respectent les créneaux du lieu et ceux déjà configurés.',
        chosenTime: 'Horaire choisi',
        duration: 'Durée : {{duration}}',
        startAt: '1. Commence à',
        endsAt: '2. Se termine à',
        allOkDesc: 'Vous pouvez enregistrer cet horaire pour tous les jours.',
        saveOnAllDays: 'Enregistrer pour tous les jours',
      },
      slotTime: {
        title: 'Créneau horaire',
        subtitle: "Choisissez d'abord le début, puis la fin.",
        allOk: 'Tout est correct',
        allOkDesc: 'Vous pouvez enregistrer cet horaire.',
        saveTime: "Enregistrer l'horaire",
        timeRangeInvalid:
          "L'heure de fin doit être postérieure à l'heure de début.",
      },
      extraordinary: {
        badge: 'Configuration exceptionnelle',
        createRange: 'Créer une plage',
        editRange: 'Modifier la plage',
        description: 'Description',
        descriptionPlaceholder: 'Ex. Ferragosto, événement privé...',
        rangeDate: 'Plage de dates',
        rangeDatePlaceholder: 'Sélectionner une plage de dates',
        clearDates: 'Effacer',
        applyDates: 'Appliquer',
        open: 'Ouvert',
        create: 'Créer',
        update: 'Mettre à jour',
        validation: 'La description et la plage de dates sont obligatoires.',
        dateRequired: 'Veuillez sélectionner une plage de dates.',
        dateOrder:
          'La date de fin ne peut pas être antérieure à la date de début.',
        timeRangeInvalid:
          "L'heure de fin doit être postérieure à l'heure de début.",
        addConfiguration: 'Ajouter une configuration exceptionnelle',
        saveConfiguration: 'Actualiser la configuration',
        addTimeSlot: 'Ajouter un créneau',
        deleteRange: 'Supprimer la plage',
        deleteConfirmTitle: 'Supprimer la plage exceptionnelle ?',
        deleteConfirmDesc:
          'Cela supprimera définitivement cette configuration de dates.',
        toast: {
          createSuccess: 'Plage exceptionnelle créée.',
          updateSuccess: 'Plage exceptionnelle mise à jour.',
          saveError: "Échec de l'enregistrement de la plage exceptionnelle.",
          deleteSuccess: 'Plage exceptionnelle supprimée.',
          deleteError: 'Échec de la suppression de la plage exceptionnelle.',
        },
      },
      toast: {
        saveSuccess: 'Configuration des horaires enregistrée.',
        saveError: "Échec de l'enregistrement de la configuration.",
        resetSuccess: 'Configuration des horaires réinitialisée.',
        resetError: 'Échec de la réinitialisation.',
        addSlotSuccess: 'Créneau horaire ajouté.',
        addSlotError: "Échec de l'ajout du créneau horaire.",
        deleteSlotSuccess: 'Créneau horaire supprimé.',
        deleteSlotError: 'Échec de la suppression du créneau horaire.',
        updateTimeSuccess: 'Créneau horaire mis à jour.',
        updateTimeError: 'Échec de la mise à jour du créneau horaire.',
      },
    },
    nl: {
      shiftOverview: 'Dienstenoverzicht',
      weeklyHours: 'Weekschema',
      addTimeSlot: 'Tijdslot toevoegen',
      day: 'Dag',
      open: 'Open',
      closed: 'Gesloten',
      hours: 'Uren',
      editSlot: 'Tijdslot bewerken',
      deleteSlot: 'Tijdslot verwijderen',
      extraordinarySection: 'Bijzondere openingen / sluitingen',
      addException: 'Uitzondering toevoegen',
      dateRange: 'Datumbereik',
      type: 'Type',
      closedOpenDay: 'Gesloten/Open',
      timeSlots: 'Tijdslots',
      cuttingMin: 'Snede (min)',
      maxGuests: 'Max gasten',
      noShifts: 'Geen diensten geconfigureerd.',
      emptyWeeklyHours:
        'Geen weekschema. Gebruik + om configuratie voor alle dagen te maken.',
      emptyExtraordinary:
        'Geen bijzondere openingen of sluitingen geconfigureerd.',
      createForAllDays: 'Algemene configuratie voor alle dagen maken',
      resetConfiguration: 'Configuratie resetten',
      saveConfiguration: 'Configuratie opslaan',
      resetConfirmTitle: 'Configuratie resetten?',
      resetConfirmDesc:
        'Dit verwijdert alle schema-instellingen voor dit formulier.',
      resetProceed: 'Doorgaan',
      tags: 'Tags',
      tagsPlaceholder: 'Voer tags in gescheiden door komma\'s',
      allowOverbook: 'Overboeking toestaan',
      alertMessage: 'Waarschuwingsbericht',
      alertMessagePlaceholder:
        'Voer een waarschuwingsbericht in voor dit tijdslot',
      days: {
        monday: 'Maandag',
        tuesday: 'Dinsdag',
        wednesday: 'Woensdag',
        thursday: 'Donderdag',
        friday: 'Vrijdag',
        saturday: 'Zaterdag',
        sunday: 'Zondag',
      },
      addTimeAllDays: {
        title: 'Tijd toevoegen aan alle dagen',
        subtitle:
          'Beschikbare tijden respecteren de tijdslots van de locatie en reeds geconfigureerde slots.',
        chosenTime: 'Gekozen tijd',
        duration: 'Duur: {{duration}}',
        startAt: '1. Begint om',
        endsAt: '2. Eindigt om',
        allOkDesc: 'Je kunt dit schema voor alle dagen opslaan.',
        saveOnAllDays: 'Opslaan op alle dagen',
      },
      slotTime: {
        title: 'Tijdslot',
        subtitle: 'Kies eerst wanneer het begint. Kies daarna wanneer het eindigt.',
        allOk: 'Alles in orde',
        allOkDesc: 'Je kunt deze tijd opslaan.',
        saveTime: 'Tijd opslaan',
        timeRangeInvalid: 'Eindtijd moet na starttijd liggen.',
      },
      extraordinary: {
        badge: 'Bijzondere configuratie',
        createRange: 'Bereik maken',
        editRange: 'Bereik bewerken',
        description: 'Beschrijving',
        descriptionPlaceholder: 'Bijv. Ferragosto, privé-evenement...',
        rangeDate: 'Datumbereik',
        rangeDatePlaceholder: 'Selecteer datumbereik',
        clearDates: 'Wissen',
        applyDates: 'Toepassen',
        open: 'Open',
        create: 'Maken',
        update: 'Bijwerken',
        validation: 'Beschrijving en datumbereik zijn verplicht.',
        dateRequired: 'Selecteer een datumbereik.',
        dateOrder: 'Einddatum kan niet vóór startdatum liggen.',
        timeRangeInvalid: 'Eindtijd moet na starttijd liggen.',
        addConfiguration: 'Bijzondere configuratie toevoegen',
        saveConfiguration: 'Configuratie vernieuwen',
        addTimeSlot: 'Tijdslot toevoegen',
        deleteRange: 'Bereik verwijderen',
        deleteConfirmTitle: 'Bijzonder bereik verwijderen?',
        deleteConfirmDesc:
          'Dit verwijdert deze datumbereikconfiguratie permanent.',
        toast: {
          createSuccess: 'Bijzonder bereik aangemaakt.',
          updateSuccess: 'Bijzonder bereik bijgewerkt.',
          saveError: 'Opslaan van bijzonder bereik mislukt.',
          deleteSuccess: 'Bijzonder bereik verwijderd.',
          deleteError: 'Verwijderen van bijzonder bereik mislukt.',
        },
      },
      toast: {
        saveSuccess: 'Schemaconfiguratie opgeslagen.',
        saveError: 'Opslaan van configuratie mislukt.',
        resetSuccess: 'Schemaconfiguratie gereset.',
        resetError: 'Resetten van configuratie mislukt.',
        addSlotSuccess: 'Tijdslot toegevoegd.',
        addSlotError: 'Toevoegen van tijdslot mislukt.',
        deleteSlotSuccess: 'Tijdslot verwijderd.',
        deleteSlotError: 'Verwijderen van tijdslot mislukt.',
        updateTimeSuccess: 'Tijdslot bijgewerkt.',
        updateTimeError: 'Bijwerken van tijdslot mislukt.',
      },
    },
    zh: {
      shiftOverview: '班次概览',
      weeklyHours: '每周营业时间',
      addTimeSlot: '添加时段',
      day: '日期',
      open: '营业',
      closed: '关闭',
      hours: '时间',
      editSlot: '编辑时段',
      deleteSlot: '删除时段',
      extraordinarySection: '特殊营业 / 关闭',
      addException: '添加例外',
      dateRange: '日期范围',
      type: '类型',
      closedOpenDay: '关闭/营业',
      timeSlots: '时段',
      cuttingMin: '切割（分钟）',
      maxGuests: '最大客人数',
      noShifts: '尚未配置班次。',
      emptyWeeklyHours: '暂无每周营业时间。使用 + 为所有日期创建配置。',
      emptyExtraordinary: '尚未配置特殊营业或关闭。',
      createForAllDays: '为所有日期创建通用配置',
      resetConfiguration: '重置配置',
      saveConfiguration: '保存配置',
      resetConfirmTitle: '重置配置？',
      resetConfirmDesc: '这将删除此表单的所有时间表设置。',
      resetProceed: '继续',
      tags: '标签',
      tagsPlaceholder: '输入以逗号分隔的标签',
      allowOverbook: '允许超额预订',
      alertMessage: '提醒消息',
      alertMessagePlaceholder: '为此时段输入提醒消息',
      days: {
        monday: '星期一',
        tuesday: '星期二',
        wednesday: '星期三',
        thursday: '星期四',
        friday: '星期五',
        saturday: '星期六',
        sunday: '星期日',
      },
      addTimeAllDays: {
        title: '为所有日期添加时间',
        subtitle: '可用时间遵循场地的时段及已配置的时段。',
        chosenTime: '已选时间',
        duration: '时长：{{duration}}',
        startAt: '1. 开始于',
        endsAt: '2. 结束于',
        allOkDesc: '您可以为所有日期保存此时间表。',
        saveOnAllDays: '保存到所有日期',
      },
      slotTime: {
        title: '时段',
        subtitle: '请先选择开始时间，再选择结束时间。',
        allOk: '一切正常',
        allOkDesc: '您可以保存此时间。',
        saveTime: '保存时间',
        timeRangeInvalid: '结束时间必须晚于开始时间。',
      },
      extraordinary: {
        badge: '特殊配置',
        createRange: '创建范围',
        editRange: '编辑范围',
        description: '描述',
        descriptionPlaceholder: '例如节假日、私人活动...',
        rangeDate: '日期范围',
        rangeDatePlaceholder: '选择日期范围',
        clearDates: '清除',
        applyDates: '应用',
        open: '营业',
        create: '创建',
        update: '更新',
        validation: '描述和日期范围为必填项。',
        dateRequired: '请选择日期范围。',
        dateOrder: '结束日期不能早于开始日期。',
        timeRangeInvalid: '结束时间必须晚于开始时间。',
        addConfiguration: '添加特殊配置',
        saveConfiguration: '刷新配置',
        addTimeSlot: '添加时段',
        deleteRange: '删除范围',
        deleteConfirmTitle: '删除特殊范围？',
        deleteConfirmDesc: '这将永久删除此日期范围配置。',
        toast: {
          createSuccess: '特殊范围已创建。',
          updateSuccess: '特殊范围已更新。',
          saveError: '保存特殊范围失败。',
          deleteSuccess: '特殊范围已删除。',
          deleteError: '删除特殊范围失败。',
        },
      },
      toast: {
        saveSuccess: '时间表配置已保存。',
        saveError: '保存配置失败。',
        resetSuccess: '时间表配置已重置。',
        resetError: '重置配置失败。',
        addSlotSuccess: '时段已添加。',
        addSlotError: '添加时段失败。',
        deleteSlotSuccess: '时段已删除。',
        deleteSlotError: '删除时段失败。',
        updateTimeSuccess: '时段已更新。',
        updateTimeError: '更新时段失败。',
      },
    },
  };

function getScheduleOverride(locale: Locale, key: string): string | undefined {
  if (!key.startsWith('forms.edit.schedule.')) {
    return undefined;
  }

  const messages = scheduleLocaleOverrides[locale];
  if (!messages) {
    return undefined;
  }

  const path = key.replace('forms.edit.schedule.', '');
  const parts = path.split('.');
  let current: unknown = messages;

  for (const part of parts) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== 'object'
    ) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return typeof current === 'string' ? current : undefined;
}

export { getScheduleOverride };
